import { ROUTES } from '../routeNames'
import type { LocationQuery } from 'vue-router'

type AccessCheck = {
  requiredPermission: unknown
  canAccess: (permission: string) => boolean | undefined
  /** False when the route's `settingsAccess` rule excludes this account. */
  settingsAllowed?: boolean
  settingsScope?: 'project' | 'account'
  teamPid: string | string[] | undefined
}

/**
 * Where to send someone who opened a page their account cannot see, or null
 * when they may stay. Settings pages fall back to the first page of the same
 * settings scope; everything else falls back to the dashboard.
 */
export const resolveAccessRedirect = ({
  requiredPermission,
  canAccess,
  settingsAllowed = true,
  settingsScope,
  teamPid
}: AccessCheck) => {
  const permitted = typeof requiredPermission !== 'string' || !!canAccess(requiredPermission)
  if (permitted && settingsAllowed) {
    return null
  }

  const normalizedTeamPid = Array.isArray(teamPid) ? teamPid[0] : teamPid
  const name =
    settingsScope === 'project'
      ? ROUTES.SETTINGS_GENERAL
      : settingsScope === 'account'
        ? ROUTES.ACCOUNT_PROFILE
        : ROUTES.DASHBOARD

  return {
    name,
    ...(normalizedTeamPid ? { params: { teamPid: normalizedTeamPid } } : {})
  }
}

type LegacyBillingCheck = {
  routeName: unknown
  perTeamBillingAllowed: boolean
  legacyBillingAllowed: boolean
  teamPid: string | string[] | undefined
  query: LocationQuery
  hash: string
}

/**
 * Legacy account-wide billing owners still get links to project billing (login
 * redirect, trial emails); their billing lives under account settings.
 */
export const resolveLegacyBillingRedirect = ({
  routeName,
  perTeamBillingAllowed,
  legacyBillingAllowed,
  teamPid,
  query,
  hash
}: LegacyBillingCheck) => {
  const normalizedTeamPid = Array.isArray(teamPid) ? teamPid[0] : teamPid
  if (routeName !== ROUTES.SETTINGS_BILLING || perTeamBillingAllowed || !legacyBillingAllowed || !normalizedTeamPid) {
    return null
  }
  return { name: ROUTES.ACCOUNT_BILLING, params: { teamPid: normalizedTeamPid }, query, hash }
}
